/* eslint-disable */
import React, { useEffect, useState } from 'react';
import { rome as moment } from '../../../../utils/date';
import Modal from '../../../../shared/modal';
import { useAppDispatch } from '../../../../app/hooks';
import {
    tryToFetchHistoricalDetail,
} from '../../../../store/admin/journey-reports/journeyReportsSlice';

interface IHistoricalReportDetailModalProps {
    open: boolean;
    tripId: string;
    onClose: () => void;
    /** Pre-loaded trip data from the historical list (so we can show metadata + download URLs without extra fetch) */
    tripData?: any;
}

type TabKey = 'cabin' | 'bar' | 'restaurant' | 'tm';

const TABS: { key: TabKey; label: string }[] = [
    { key: 'cabin', label: 'Cabin Report' },
    { key: 'bar', label: 'Bar Report' },
    { key: 'restaurant', label: 'Restaurant Report' },
    { key: 'tm', label: 'TM Report' },
];

const handleDownloadFile = async (url: string, filename: string) => {
    try {
        const response = await fetch(url);
        const blob = await response.blob();
        const blobUrl = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = blobUrl;
        link.download = filename;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        setTimeout(() => URL.revokeObjectURL(blobUrl), 100);
    } catch {
        window.open(url, '_blank');
    }
};

const HistoricalReportDetailModal: React.FC<IHistoricalReportDetailModalProps> = ({
    open,
    tripId,
    onClose,
    tripData,
}) => {
    const dispatch = useAppDispatch();

    const [activeTab, setActiveTab] = useState<TabKey>('cabin');
    const [data, setData] = useState<any>(null);
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);

    /* ── Fetch detail on open ── */
    useEffect(() => {
        if (!open || !tripId) return;
        setIsLoading(true);
        setError(null);
        dispatch(tryToFetchHistoricalDetail(tripId))
            .unwrap()
            .then((res: any) => setData(res))
            .catch((err: any) => setError(typeof err === 'string' ? err : 'Failed to load report detail'))
            .finally(() => setIsLoading(false));
    }, [open, tripId]);

    /* ── Reset ── */
    useEffect(() => {
        if (!open) {
            setData(null);
            setActiveTab('cabin');
            setError(null);
        }
    }, [open]);

    /* ── Merge fetched data with pre-loaded tripData for metadata / URLs ── */
    const merged = data || tripData || null;

    /* ── Derived report data ── */
    const getHodSections = (type: TabKey): any[] => {
        if (type === 'tm') return [];
        /* Detail endpoint might return sections array per HOD type */
        return data?.hodReports?.[type]?.sections || [];
    };

    const getHodSubmittedAt = (type: TabKey): string | null => {
        const hodInfo = merged?.hodReports?.[type];
        return hodInfo?.submittedAt || null;
    };

    const tmReport = data?.tmReport || null;
    const tmSubmittedAt = merged?.tmReportSubmittedAt || tmReport?.submittedAt || null;

    /* ── Download URLs (prefer data from detail, fallback to list tripData) ── */
    const docxUrl = data?.docxUrl || tripData?.docxUrl || null;
    const pdfUrl = data?.pdfUrl || tripData?.pdfUrl || null;

    /* ── Metadata ── */
    const itinerary = merged?.itineraryTitle || merged?.itinerary?.journeyTitle || merged?.journeyTitle || '—';
    const startDate = merged?.startDate ? moment(merged.startDate).format('DD MMM YYYY') : '—';
    const endDate = merged?.endDate ? moment(merged.endDate).format('DD MMM YYYY') : '';
    /* Use tripData (list item) for trip status since its shape is known; the detail endpoint may not include it */
    const tripStatus = tripData?.status || data?.tripStatus || '—';
    const reportStatus = merged?.reportStatus || data?.status || null;

    return (
        <Modal
            open={open}
            onClose={onClose}
            contentContainerStyle='max-w-[95%] w-[800px] rounded-lg mb-4'
            title='Journey Report Detail'
        >
            <div className='font-inter'>
                {/* Metadata header */}
                {merged && !isLoading && (
                    <div className='mb-4 p-3 bg-[#FAFAFA] dark:bg-[#232323] rounded-lg flex items-center text-xs text-gray-600 overflow-x-auto'>
                        <span className='whitespace-nowrap dark:text-white/80'><b className='text-gray-800 dark:text-white'>Itinerary:</b> {itinerary}</span>
                        <span className='mx-3 text-gray-300'>|</span>
                        <span className='whitespace-nowrap dark:text-white/80'><b className='text-gray-800 dark:text-white'>Dates:</b> {startDate}{endDate ? ` – ${endDate}` : ''}</span>
                        <span className='mx-3 text-gray-300'>|</span>
                        <span className='whitespace-nowrap dark:text-white/80'><b className='text-gray-800 dark:text-white'>Trip:</b> <span className='capitalize'>{tripStatus}</span></span>
                        {reportStatus && (
                            <>
                                <span className='mx-3 text-gray-300'>|</span>
                                <span className='whitespace-nowrap dark:text-white/80'><b className='text-gray-800 dark:text-white'>Report:</b> <span className='capitalize'>{reportStatus.replace('_', ' ')}</span></span>
                            </>
                        )}
                    </div>
                )}

                {/* Tabs */}
                {!isLoading && (
                    <div className='flex gap-1 p-1 bg-gray-100 dark:bg-[#232323] rounded-lg w-fit mb-4'>
                        {TABS.map((tab) => (
                            <button
                                key={tab.key}
                                onClick={() => setActiveTab(tab.key)}
                                className={`px-3 py-1.5 text-xs font-medium rounded-md transition ${activeTab === tab.key ? 'bg-white dark:bg-black shadow-sm text-gray-800 dark:text-white' : 'text-gray-500  hover:text-gray-700 dark:text-white'}`}
                            >
                                {tab.label}
                            </button>
                        ))}
                    </div>
                )}

                {/* Loading */}
                {isLoading && (
                    <div className='flex items-center justify-center py-12'>
                        <div className='w-6 h-6 border-2 border-gray-300 border-t-blue-500 rounded-full animate-spin' />
                    </div>
                )}

                {/* Error */}
                {error && (
                    <div className='mb-4 p-3 rounded-lg bg-red-50 text-red-700 text-sm'>
                        {error}
                    </div>
                )}

                {/* HOD tabs (cabin / bar / restaurant) */}
                {!isLoading && activeTab !== 'tm' && (
                    <div className='max-h-[55vh] overflow-y-auto pr-1 space-y-4'>
                        {/* Submitted info */}
                        {getHodSubmittedAt(activeTab) && (
                            <div className='flex items-center gap-2 mb-2'>
                                <span className='inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800'>
                                    <span className='w-1.5 h-1.5 rounded-full bg-green-500' />
                                    Submitted
                                </span>
                                <span className='text-xs text-gray-500'>
                                    {moment(getHodSubmittedAt(activeTab)).format('DD MMM YYYY, HH:mm')}
                                </span>
                            </div>
                        )}

                        {getHodSections(activeTab).length > 0 ? (
                            getHodSections(activeTab).map((section: any, idx: number) => (
                                <div
                                    key={(section.cabinId || '') + idx}
                                    className='border border-[#E9EAEB] rounded-lg p-4'
                                >
                                    <h4 className='text-sm font-semibold text-gray-800 dark:text-white mb-2'>
                                        {section.cabinId || `Section ${idx + 1}`}
                                    </h4>
                                    <p className='text-sm text-gray-700 dark:text-white whitespace-pre-wrap'>
                                        {section.editedSummary || section.aiSummary || '—'}
                                    </p>
                                </div>
                            ))
                        ) : (
                            <div className='text-center py-12 text-gray-400 dark:text-white text-sm'>
                                {getHodSubmittedAt(activeTab)
                                    ? 'Report sections not available in detail view.'
                                    : `No ${activeTab} report available for this trip.`
                                }
                            </div>
                        )}
                    </div>
                )}

                {/* TM tab */}
                {!isLoading && activeTab === 'tm' && (
                    <div className='max-h-[55vh] overflow-y-auto pr-1'>
                        {/* Submitted info */}
                        {tmSubmittedAt && (
                            <div className='flex items-center gap-2 mb-3'>
                                <span className='inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800'>
                                    <span className='w-1.5 h-1.5 rounded-full bg-green-500' />
                                    Submitted
                                </span>
                                <span className='text-xs text-gray-500'>
                                    {moment(tmSubmittedAt).format('DD MMM YYYY, HH:mm')}
                                </span>
                            </div>
                        )}

                        {tmReport ? (
                            <div className='border border-[#E9EAEB] rounded-lg p-4'>
                                <p className='text-sm text-gray-700 dark:text-white whitespace-pre-wrap'>
                                    {tmReport.editedDraft || tmReport.draft || '—'}
                                </p>
                            </div>
                        ) : tmSubmittedAt ? (
                            <div className='text-center py-8 text-gray-400 text-sm'>
                                TM report content not available in detail view. Use the download buttons below.
                            </div>
                        ) : (
                            <div className='text-center py-12 text-gray-400 text-sm'>
                                No TM report available for this trip.
                            </div>
                        )}
                    </div>
                )}

                {/* Download buttons (shown in TM tab if URLs exist) */}
                {!isLoading && activeTab === 'tm' && (docxUrl || pdfUrl) && (
                    <div className='flex gap-2 mt-4 pt-4 border-t border-[#E9EAEB]'>
                        {docxUrl && (
                            <button
                                onClick={() => handleDownloadFile(docxUrl, `journey-report-${tripId}.docx`)}
                                className='px-3 py-2 text-xs font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition flex items-center gap-1.5'
                            >
                                <img src='/svg/file-docx-color-green-icon.svg' alt='DOCX' className='w-4 h-4' />
                                Download DOCX
                            </button>
                        )}
                        {pdfUrl && (
                            <button
                                onClick={() => handleDownloadFile(pdfUrl, `journey-report-${tripId}.pdf`)}
                                className='px-3 py-2 text-xs font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition flex items-center gap-1.5'
                            >
                                <img src='/svg/download-pdf-icon.svg' alt='PDF' className='w-4 h-4 dark:contrast-0' />
                                Download PDF
                            </button>
                        )}
                    </div>
                )}
            </div>
        </Modal>
    );
};

export default HistoricalReportDetailModal;
